import React, {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import style from './style.module.scss';
import Grid from './Grid';
import ZoomPanMap, { useMapView } from './Map';

const EMPTY_TILES = [];

/** How many region mosaics may decode at once. Finished ones stay mounted. */
const MAX_IN_FLIGHT = 2;
const SETTLE_FALLBACK_MS = 12000;

const StadiumLiveContext = createContext({
  isLive: () => false,
  report: () => {},
  markSettled: () => {},
});

const sameSet = (a, b) => {
  if (a.size !== b.size) return false;
  let same = true;
  a.forEach((id) => { if (!b.has(id)) same = false; });
  return same;
};

const StadiumBudget = ({ children, loading }) => {
  const [liveIds, setLiveIds] = useState(() => new Set());
  const metricsRef = useRef(new Map());
  const liveRef = useRef(liveIds);
  liveRef.current = liveIds;
  const readyRef = useRef(new Set());
  const rafRef = useRef(0);

  const flush = useCallback(() => {
    rafRef.current = 0;
    if (loading) return;
    const prev = liveRef.current;
    const next = new Set(prev);
    const inflight = [...next].filter((id) => !readyRef.current.has(id)).length;
    if (inflight >= MAX_IN_FLIGHT) return;

    const ranked = [];
    metricsRef.current.forEach((m, id) => {
      if (next.has(id) || !m.queueable) return;
      ranked.push({ id, intersecting: m.intersecting, area: m.area || 0 });
    });
    ranked.sort((a, b) => {
      if (a.intersecting !== b.intersecting) return a.intersecting ? -1 : 1;
      return b.area - a.area;
    });

    let added = inflight;
    ranked.forEach((row) => {
      if (added >= MAX_IN_FLIGHT) return;
      next.add(row.id);
      added += 1;
    });

    if (sameSet(next, prev)) return;
    setLiveIds(next);
  }, [loading]);

  const report = useCallback((id, metrics) => {
    metricsRef.current.set(id, metrics);
    if (!rafRef.current) rafRef.current = requestAnimationFrame(flush);
  }, [flush]);

  const markSettled = useCallback((id) => {
    if (readyRef.current.has(id)) return;
    readyRef.current.add(id);
    if (!rafRef.current) rafRef.current = requestAnimationFrame(flush);
  }, [flush]);

  useEffect(() => {
    if (!loading) flush();
  }, [loading, flush]);

  useEffect(() => () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  }, []);

  const value = useMemo(() => ({
    isLive: (id) => liveIds.has(id),
    report,
    markSettled,
  }), [liveIds, report, markSettled]);

  return (
    <StadiumLiveContext.Provider value={value}>
      {children}
    </StadiumLiveContext.Provider>
  );
};

/** Placeholder until this region's turn; once mounted it stays (full scan). */
const SlotGate = ({
  slotId, label, queueable, children, className, style: slotStyle,
}) => {
  const ref = useRef(null);
  const { viewportEl, scale } = useMapView();
  const { isLive, report, markSettled } = useContext(StadiumLiveContext);
  const live = isLive(slotId);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const measure = () => {
      const rect = el.getBoundingClientRect();
      let intersecting = rect.width > 1 && rect.height > 1;
      if (viewportEl) {
        const vr = viewportEl.getBoundingClientRect();
        intersecting = rect.right > vr.left && rect.left < vr.right
          && rect.bottom > vr.top && rect.top < vr.bottom;
      }
      report(slotId, {
        queueable,
        intersecting,
        area: Math.max(0, rect.width) * Math.max(0, rect.height),
      });
    };

    measure();
    const io = new IntersectionObserver(measure, {
      root: viewportEl || null,
      rootMargin: '12%',
      threshold: 0,
    });
    io.observe(el);
    return () => {
      io.disconnect();
      report(slotId, { queueable: false, intersecting: false, area: 0 });
    };
  }, [viewportEl, scale, slotId, queueable, report]);

  useEffect(() => {
    if (!live) return undefined;
    const t = window.setTimeout(() => markSettled(slotId), SETTLE_FALLBACK_MS);
    return () => window.clearTimeout(t);
  }, [live, slotId, markSettled]);

  return (
    <div ref={ref} className={className} style={slotStyle}>
      {live ? React.cloneElement(children, { onSettled: () => markSettled(slotId) }) : (
        <div className={style.slotPlaceholder} title={label || undefined}>
          {label ? <span className={style.slotPlaceholderLabel}>{label}</span> : null}
        </div>
      )}
    </div>
  );
};

/** Box/SRS visibility is CSS on the preview root — not props into every slot.
 *  Stadium passes showBoxes always-on so hit-testing stays live whenever labels
 *  are selected; the eye toggle only flips CSS visibility via data-boxes-show. */
const StadiumSheet = memo(({
  preview, onOpenDetail, loading, srsShow,
}) => {
  const { stageW, stageH, slots } = preview.stadiumLayout || { stageW: 0, stageH: 0, slots: [] };

  return (
    <StadiumBudget loading={loading}>
      <div className={style.stadiumStage} style={{ width: stageW, height: stageH }}>
        {slots.map((slot, i) => {
          const slotId = slot.scanId || `slot-${i}`;
          return (
            <SlotGate
              key={slotId}
              slotId={slotId}
              label={slot.label}
              queueable={!loading}
              className={style.stadiumSlot}
              style={{
                left: slot.left,
                top: slot.top,
                width: slot.width,
                height: slot.height,
                transform: `translate(-50%, -50%) rotate(${slot.angle}deg)`,
              }}
            >
              <Grid
                scan={slot.scan}
                tiles={slot.tiles}
                rows={slot.rows}
                cols={slot.cols}
                tileBoxes={preview.tileBoxes}
                selectedLabels={preview.selectedLabelSet}
                showBoxes
                onOpenDetail={onOpenDetail}
                loading={loading}
                boxKeyPrefix={slot.scanId}
                scanKey={slot.scanId || ''}
                variant="slot"
                slotWidth={slot.width}
                slotHeight={slot.height}
                overlay={preview.overlay}
                bakeSrs={srsShow}
              />
            </SlotGate>
          );
        })}
      </div>
    </StadiumBudget>
  );
});

const Preview = ({ data, onOpenDetail }) => {
  const {
    preview, ui, loading, controls,
  } = data;
  const srsAttr = ui.srsShow ? '1' : '0';
  const boxesAttr = ui.showBoxes ? '1' : '0';

  if (preview.showStadium) {
    return (
      <div
        className={style.previewPane}
        data-srs-show={srsAttr}
        data-boxes-show={boxesAttr}
      >
        <ZoomPanMap key={controls.optionValue} fit="contain">
          <StadiumSheet
            preview={preview}
            onOpenDetail={onOpenDetail}
            loading={loading.scan}
            srsShow={ui.srsShow}
          />
        </ZoomPanMap>
      </div>
    );
  }

  if (!preview.showRegion) return null;

  return (
    <div
      className={style.previewPaneHug}
      data-srs-show={srsAttr}
      data-boxes-show={boxesAttr}
    >
      <ZoomPanMap key={controls.optionValue} fit="content">
        <Grid
          scan={preview.singleScan}
          tiles={preview.shimmer ? EMPTY_TILES : preview.tiles}
          rows={preview.rows}
          cols={preview.cols}
          tileBoxes={preview.tileBoxes}
          selectedLabels={preview.selectedLabelSet}
          showBoxes
          onOpenDetail={onOpenDetail}
          loading={preview.shimmer}
          scanKey={controls.optionValue || ''}
          overlay={preview.overlay}
        />
      </ZoomPanMap>
    </div>
  );
};

export default Preview;
export { sameSet };
